import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as ImagePicker from 'expo-image-picker';
import * as MediaLibrary from 'expo-media-library';
import BackgroundGeolocation from 'react-native-background-geolocation';
import { Coordinate } from '@/types/trail';
import { openAppSettings } from '@/utils/permissions';
import i18n from '@/i18n';

// Track if user has already been prompted for background location
const BG_PERMISSION_PROMPTED_KEY = 'tailtrails_bg_permission_prompted';

type InitialLocationResult = {
  coordinate: Coordinate | null;
  accuracy?: number;
};

export async function requestBgPermissionAndInitialLocation(timeout: number = 30): Promise<InitialLocationResult> {
  // Check if user has already been prompted for background location
  const hasBeenPrompted = await AsyncStorage.getItem(BG_PERMISSION_PROMPTED_KEY);
  
  // Only show rationale and request permission if this is the first time
  if (hasBeenPrompted !== 'true') {
    // Ask the user for a brief rationale before invoking the native background permission prompt.
    // Background location allows TailTrails to continue recording your walk when the app is backgrounded
    // or the screen is locked.
    // @ts-ignore
    await new Promise<void>((resolve) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { Alert } = require('react-native');
        Alert.alert(
          i18n.t('record.bgRationaleTitle'),
          i18n.t('record.bgRationaleMessage'),
          [{ text: 'Continue', onPress: () => resolve() }],
          { cancelable: false },
        );
      } catch (e) {
        resolve();
      }
    });

    // @ts-ignore
    await BackgroundGeolocation.requestPermission();
    
    // Mark that user has been prompted so we don't ask again
    await AsyncStorage.setItem(BG_PERMISSION_PROMPTED_KEY, 'true');
  }

  try {
    // @ts-ignore
    const loc = await BackgroundGeolocation.getCurrentPosition({ timeout });
    if (loc?.coords) {
      return {
        coordinate: { latitude: loc.coords.latitude, longitude: loc.coords.longitude },
        accuracy: loc.coords.accuracy ?? undefined,
      };
    }
  } catch {
    // Non-fatal: caller may continue without immediate fix
  }
  return { coordinate: null, accuracy: undefined };
}

export async function resolveInitialRecordingCoordinate(
  currentLocation?: Coordinate | null,
  timeout: number = 30,
): Promise<InitialLocationResult> {
  if (currentLocation) {
    return { coordinate: currentLocation, accuracy: undefined };
  }
  return requestBgPermissionAndInitialLocation(timeout);
}

export async function captureAndStoreRecordingPhoto() {
  if (Platform.OS === 'web') {
    return { status: 'not-supported' as const };
  }

  // Check current camera permission status first
  const { status: existingStatus } = await ImagePicker.getCameraPermissionsAsync();
  if (existingStatus === 'granted') {
    // Already granted — go straight to the camera
  } else if (existingStatus === 'denied') {
    // Already denied — skip rationale, offer Settings link
    try {
      const { Alert } = require('react-native');
      Alert.alert(
        i18n.t('record.cameraDeniedTitle'),
        i18n.t('record.cameraDeniedMessage'),
        [
          { text: i18n.t('common.cancel'), style: 'cancel' },
          { text: i18n.t('common.openSettings'), onPress: openAppSettings },
        ],
      );
    } catch { /* ignore */ }
    return { status: 'permission-denied' as const };
  } else {
    // Ask the user why we need camera access before invoking the native prompt
    await new Promise<void>((resolve) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { Alert } = require('react-native');
        Alert.alert(
          i18n.t('record.cameraRationaleTitle'),
          i18n.t('record.cameraRationaleMessage'),
          [{ text: 'Continue', onPress: () => resolve() }],
          { cancelable: false },
        );
      } catch (e) {
        resolve();
      }
    });

    const { status } = await ImagePicker.requestCameraPermissionsAsync();
    if (status !== 'granted') {
      try {
        const { Alert } = require('react-native');
        Alert.alert(
          i18n.t('record.cameraDeniedTitle'),
          i18n.t('record.cameraDeniedMessage'),
          [
            { text: i18n.t('common.cancel'), style: 'cancel' },
            { text: i18n.t('common.openSettings'), onPress: openAppSettings },
          ],
        );
      } catch { /* ignore */ }
      return { status: 'permission-denied' as const };
    }
  }

  const result = await ImagePicker.launchCameraAsync({
    mediaTypes: ImagePicker.MediaTypeOptions.Images,
    quality: 0.7,
    base64: false,
  });

  if (result.canceled || !result.assets || result.assets.length === 0) {
    return { status: 'cancelled' as const };
  }

  const uri = result.assets[0].uri;
  let savedToGallery = false;
  try {
    // Explain why we might save to the user's gallery
    const mediaAgreed: boolean = await new Promise((resolve) => {
      try {
        // eslint-disable-next-line @typescript-eslint/no-var-requires
        const { Alert } = require('react-native');
        Alert.alert(
          i18n.t('record.savePhotoTitle'),
          i18n.t('record.savePhotoMessage'),
          [
            { text: i18n.t('record.dontSave'), style: 'cancel', onPress: () => resolve(false) },
            { text: i18n.t('record.allowAndSave'), onPress: () => resolve(true) },
          ],
        );
      } catch (e) {
        resolve(true);
      }
    });

    if (mediaAgreed) {
      const { status: mediaStatus } = await MediaLibrary.requestPermissionsAsync();
      if (mediaStatus === 'granted') {
        try {
          await MediaLibrary.saveToLibraryAsync(uri);
          savedToGallery = true;
        } catch (err) {
          console.warn('Failed to save photo to gallery', err);
        }
      } else {
        console.warn('Media library permission not granted, photo not saved to gallery');
      }
    }
  } catch (err) {
    console.warn('Media library error', err);
  }
  const photosStr = await AsyncStorage.getItem('recording_photos');
  const photos = photosStr ? JSON.parse(photosStr) : [];
  photos.push({ uri, timestamp: Date.now() });
  await AsyncStorage.setItem('recording_photos', JSON.stringify(photos));

  return { status: 'saved' as const, savedToGallery };
}
